import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  CreateListingInput,
  CreateDirectListingInput,
  Listing,
  ListingStatus,
  Nullable,
  UpdateListingInput,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';

// Re-export types for convenience
export type { Listing, ListingStatus, CreateListingInput, CreateDirectListingInput, UpdateListingInput };

// =============================================================================
// FRAGMENTS
// =============================================================================

const LISTING_FRAGMENT = gql`
  fragment ListingFields on Listing {
    id
    quantity
    priceType
    salePrice
    salePriceUSD
    customDollarRate
    marketPricePercentage
    isNegotiable
    status
    priceUpdatedAt
    inventory {
      id
      konamiId
      quantity
      language
      rarity
      state
      cardSet {
        id
        code
        card {
          name
        }
      }
      warehouse {
        id
        name
      }
    }
    createdAt
    updatedAt
  }
`;

// =============================================================================
// QUERIES
// =============================================================================

export const getMyListings = async (
  user: Nullable<UserWithToken>,
  variables?: { status?: ListingStatus; page?: number; limit?: number }
) =>
  getGraphQLQueryClient(user).request<WithMeta<Listing>>(
    gql`
      ${LISTING_FRAGMENT}
      query myListings($status: ListingStatus, $page: Int, $limit: Int) {
        data: myListings(status: $status, page: $page, limit: $limit) {
          data {
            ...ListingFields
          }
          meta {
            total
            perPage
            lastPage
            page
          }
        }
      }
    `,
    variables
  );

export const getListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Listing | null>>(
    gql`
      ${LISTING_FRAGMENT}
      query listing($id: Int!) {
        data: listing(id: $id) {
          ...ListingFields
          events {
            id
            type
            message
            createdAt
            user {
              id
              name
              lastName
            }
          }
        }
      }
    `,
    variables
  );

export const getListingsForInventory = async (
  user: Nullable<UserWithToken>,
  variables: { inventoryId: number }
) =>
  getGraphQLQueryClient(user).request<{ data: Listing[] }>(
    gql`
      ${LISTING_FRAGMENT}
      query listingsForInventory($inventoryId: Int!) {
        data: listingsForInventory(inventoryId: $inventoryId) {
          ...ListingFields
        }
      }
    `,
    variables
  );

// =============================================================================
// MUTATIONS
// =============================================================================

export const createListing = async (
  user: Nullable<UserWithToken>,
  variables: { input: CreateListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Listing>>(
    gql`
      ${LISTING_FRAGMENT}
      mutation createListing($input: CreateListingInput!) {
        data: createListing(input: $input) {
          ...ListingFields
        }
      }
    `,
    variables
  );

export const createDirectListing = async (
  user: Nullable<UserWithToken>,
  variables: { input: CreateDirectListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Listing>>(
    gql`
      ${LISTING_FRAGMENT}
      mutation createDirectListing($input: CreateDirectListingInput!) {
        data: createDirectListing(input: $input) {
          ...ListingFields
        }
      }
    `,
    variables
  );

export const updateListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number; input: UpdateListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Listing>>(
    gql`
      ${LISTING_FRAGMENT}
      mutation updateListing($id: Int!, $input: UpdateListingInput!) {
        data: updateListing(id: $id, input: $input) {
          ...ListingFields
        }
      }
    `,
    variables
  );

export const pauseListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Listing>>(
    gql`
      ${LISTING_FRAGMENT}
      mutation pauseListing($id: Int!) {
        data: pauseListing(id: $id) {
          ...ListingFields
        }
      }
    `,
    variables
  );

export const activateListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Listing>>(
    gql`
      ${LISTING_FRAGMENT}
      mutation activateListing($id: Int!) {
        data: activateListing(id: $id) {
          ...ListingFields
        }
      }
    `,
    variables
  );

export const deleteListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number; decrementInventory?: boolean }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteListing($id: Int!, $decrementInventory: Boolean) {
        data: deleteListing(id: $id, decrementInventory: $decrementInventory)
      }
    `,
    variables
  );
